import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/config";
import { X, Mail, User, CheckCircle2, AlertCircle, Loader2, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * NewsletterModal
 * @param {boolean}  isOpen    - Controla visibilidad del modal
 * @param {Function} onClose   - Callback para cerrar
 * @param {string}   listaTipo - "general" | "precios"
 */
export const NewsletterModal = ({ isOpen, onClose, listaTipo = "general" }) => {
  const [nombre, setNombre] = useState("");
  const [email, setEmail]   = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef(null);

  // Focus al abrir
  useEffect(() => {
    if (isOpen) {
      setStatus("idle");
      setNombre("");
      setEmail("");
      setWebsite("");
      setErrorMsg("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Cerrar con Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isPrecios  = listaTipo === "precios";
  const tituloModal = isPrecios
    ? "Actualizaciones de Tarifas"
    : "Newsletter de Movilidad Eléctrica";
  const subtituloModal = isPrecios
    ? "Recibí un aviso en tu correo cada vez que actualicemos los precios de combustibles y electricidad."
    : "Mantente al tanto de las últimas noticias sobre electromovilidad en Uruguay.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre.trim() || !email.trim()) return;

    // Validación de email en frontend antes de ir al backend
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setErrorMsg("Ingresá un email válido (ej: nombre@dominio.com).");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        nombre: nombre.trim(),
        email: email.trim(),
        lista_tipo: listaTipo,
        website,
      });
      setStatus("success");
    } catch (err) {
      // El 422 de Pydantic devuelve detail como array de objetos, no string.
      // Si llegamos aquí igual (ej: 429, 502, 503), normalizamos el mensaje.
      const rawDetail = err.response?.data?.detail;
      const msg = Array.isArray(rawDetail)
        ? "Verificá los datos ingresados e intentá de nuevo."
        : (rawDetail || "No pudimos procesar tu suscripción. Intentá de nuevo.");
      setErrorMsg(msg);
      setStatus("error");
    }
  };

  return (
    // Overlay
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      aria-modal="true"
      role="dialog"
      aria-labelledby="newsletter-modal-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border border-white/10
                      bg-card/90 backdrop-blur-xl animate-in fade-in slide-in-from-bottom-4 duration-300">

        {/* Header con gradiente */}
        <div className={`px-6 py-5 ${isPrecios
          ? "bg-gradient-to-r from-secondary/80 to-secondary/50"
          : "bg-gradient-to-r from-primary/80 to-primary/50"
        }`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-white/20">
                {isPrecios
                  ? <Bell  className="h-6 w-6 text-white" />
                  : <Mail  className="h-6 w-6 text-white" />
                }
              </div>
              <div>
                <h2 id="newsletter-modal-title" className="text-lg font-bold text-white leading-tight">
                  {tituloModal}
                </h2>
                <p className="text-sm text-white/75 mt-0.5">{subtituloModal}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors shrink-0 mt-0.5"
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {status === "success" ? (
            <div className="text-center py-6 space-y-3 animate-in fade-in duration-300">
              <CheckCircle2 className="h-14 w-14 mx-auto text-green-500" />
              <h3 className="text-xl font-bold text-foreground">¡Ya estás suscripto!</h3>
              <p className="text-sm text-muted-foreground">
                Gracias, <span className="font-semibold text-foreground">{nombre}</span>.{" "}
                {isPrecios
                  ? "Te avisaremos cuando actualicemos las tarifas."
                  : "Recibirás las últimas novedades en tu bandeja de entrada."}
              </p>
              <Button className="mt-2" onClick={onClose}>Cerrar</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Nombre */}
              <div className="space-y-1.5">
                <Label htmlFor="nl-nombre" className="text-sm font-medium">
                  Nombre
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="nl-nombre"
                    ref={inputRef}
                    type="text"
                    placeholder="Tu nombre"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="pl-9"
                    required
                    disabled={status === "loading"}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="nl-email" className="text-sm font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="nl-email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                    disabled={status === "loading"}
                  />
                </div>
              </div>

              {/* Error */}
              {status === "error" && (
                <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg animate-in fade-in duration-200">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {errorMsg}
                </div>
              )}

              {/* Honeypot — campo trampa para bots; nunca visible para humanos */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                <label htmlFor="nl-website">Website</label>
                <input
                  id="nl-website"
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full"
                disabled={status === "loading" || !nombre.trim() || !email.trim()}
              >
                {status === "loading"
                  ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Suscribiendo...</>
                  : isPrecios
                    ? <><Bell className="h-4 w-4 mr-2" /> Notificarme</>
                    : <><Mail className="h-4 w-4 mr-2" /> Suscribirme</>
                }
              </Button>

              <p className="text-[11px] text-center text-muted-foreground">
                Sin spam. Podés darte de baja cuando quieras.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
